<template>
  <BaseScreen v-bind="screen">
    <template #actions>
      <BaseButton
        v-for="(item, i) in actions"
        :key="`action_${i}`"
        :text="item.text"
        :is-outlined="item.isOutlined"
        size="s"
        @click="item.action"
      />
    </template>
    <BaseTimer
      ref="timer"
      slot="title"
      :style="{ visibility: isTimerStarted ? 'visible' : 'hidden' }"
      @start="onTimerStart"
      @stop="onTimerStop"
      @finish="onTimerFinish"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Camera
      ref="camera"
      class="screen__camera"
      @start="onCameraStart"
      @error="showError"
      @photo="onPhotoReady"
    >
      <PhotoFrames
        v-if="isPhotoFramesVisible"
        ref="photoframe"
        :photo="photo"
        slot="overlay"
      />
      <img
        v-else-if="isPhotoReady"
        :src="photo.base64"
        slot="overlay"
        class="camera__image"
        alt=""
      />
    </Camera>
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import photoPrintMixin from '@/mixins/photoPrint';
import BaseButton from '@/components/BaseButton.vue';
import BaseTimer from '@/components/BaseTimer.vue';
import PhotoFrames from '@/components/PhotoFrames.vue';
import Camera from '@/components/Camera.vue';

import CONSTANTS from '@/helpers/constants';

const {
  ACTION_TAKE_PHOTO,
  ACTION_REMAKE,
  ACTION_PRINT,
  ACTION_CANCEL,
  ACTION_CONTINUE,
  ACTION_PICK,
  ACTION_SHOW_PHOTO_FRAMES,
} = CONSTANTS;

export default {
  name: 'TakePhotoScreen',
  mixins: [screenMixin, photoPrintMixin],
  components: {
    BaseButton,
    BaseTimer,
    PhotoFrames,
    Camera,
  },
  data() {
    return {
      isTimerStarted: false,
      isPrintStarted: false,
      isCameraStarted: false,
      isPhotoFramesVisible: false,
      isPhotoFrameSelected: false,
      hasPrinter: true,
    };
  },
  computed: {
    ...mapState([
      'photo',
    ]),
    pendingText() {
      if (!this.isCameraStarted) return this.$t('pending.hardware');
      if (this.isPrintStarted) return this.$t('pending.print');
      return this.$t('pending.processing');
    },
    actions() {
      if (this.isTimerStarted) {
        return [
          {
            text: this.$t('actions.cancel'),
            action: this.stopTimer,
            isOutlined: true,
          },
        ];
      }
      if (this.isPhotoFramesVisible) {
        return [
          this.isPhotoFrameSelected ? {
            text: this.$t('actions.removeFrame'),
            action: this.removePhotoFrame,
            isOutlined: true,
          } : {
            text: this.$t('actions.cancel'),
            action: this.hidePhotoFrames,
            isOutlined: true,
          },
          {
            text: this.$t('actions.pick'),
            action: this.selectPhotoFrame,
          },
        ];
      }
      if (this.isPhotoReady) {
        return [
          {
            text: this.$t('actions.reshoot'),
            action: this.reshoot,
            isOutlined: true,
          },
          {
            text: this.$t('actions.sendToEmail'),
            action: this.goNext,
          },
          this.hasPrinter ? {
            text: this.$t('actions.print'),
            action: this.printPhoto,
          } : null,
          {
            text: this.$t('actions.selectFrame'),
            action: this.showPhotoFrames,
          },
        ].filter(Boolean);
      }
      return [
        {
          text: this.$t('actions.takePhoto'),
          action: this.startTimer,
        },
      ];
    },
    error() {
      return {
        title: this.$t('titles.badHardware'),
        text: this.$t('texts.photoNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    isPhotoReady() {
      return Boolean(this.photo);
    },
    hasActiveOperation() {
      return this.isPrintStarted || this.isTimerStarted || this.isPhotoFramesVisible;
    },
  },
  created() {
    this.showPending(0);
    this.clearPhoto();
    this.initActions();
  },
  mounted() {
    this.startCamera();
  },
  methods: {
    ...mapMutations([
      'setPhoto',
      'clearPhoto',
    ]),
    printPhoto() {
      if (!this.isPhotoReady || this.hasActiveOperation) return;
      this.startPhotoPrint(this.photo.fullPath);
    },
    showPhotoFrames() {
      if (!this.isPhotoReady || this.hasActiveOperation) return;
      this.isPhotoFramesVisible = true;
    },
    hidePhotoFrames() {
      this.isPhotoFramesVisible = false;
    },
    selectPhotoFrame() {
      if (!this.isPhotoFramesVisible) return;
      this.$refs.photoframe?.selectFrame(); // eslint-disable-line
      this.isPhotoFrameSelected = true;
      this.hidePhotoFrames();
    },
    removePhotoFrame() {
      if (!this.isPhotoFramesVisible) return;
      this.$refs.photoframe?.removeFrame(); // eslint-disable-line
      this.isPhotoFrameSelected = false;
      this.hidePhotoFrames();
    },
    reshoot() {
      if (this.hasActiveOperation) return;
      this.clearPhoto();
      this.startTimer();
    },
    onNoPrinter() {
      this.hasPrinter = false;
    },
    onStartPhotoPrint() {
      this.isPrintStarted = true;
      this.showPending();
    },
    onFinishPhotoPrint() {
      this.isPrintStarted = false;
      this.hidePending();
    },
    onCameraStart() {
      this.isCameraStarted = true;
      this.hidePending();
    },
    onPhotoReady(src) {
      this.setPhoto(src);
      this.hidePending();
    },
    onTimerStart() {
      this.isTimerStarted = true;
    },
    onTimerStop() {
      this.isTimerStarted = false;
    },
    onTimerFinish() {
      this.$refs.camera?.takePhoto(); // eslint-disable-line
      this.showPending();
    },
    startCamera() {
      return this.$refs.camera?.start();
    },
    startTimer() {
      return this.$refs.timer?.start();
    },
    stopTimer() {
      return this.$refs.timer?.stop();
    },
    goNext() {
      if (!this.isPhotoReady || this.hasActiveOperation) return;
      this.$router.push(this.$router.options.paths.sendPhoto);
    },
    cancelOperation() {
      if (this.isTimerStarted) {
        this.stopTimer();
        return;
      }
      if (this.isPhotoFramesVisible) {
        const action = this.isPhotoFrameSelected
          ? this.removePhotoFrame
          : this.hidePhotoFrames;
        action();
      }
    },
    initActions() {
      const rootActions = [
        [ACTION_TAKE_PHOTO, this.reshoot],
        [ACTION_REMAKE, this.reshoot],
        [ACTION_PRINT, this.printPhoto],
        [ACTION_CANCEL, this.cancelOperation],
        [ACTION_CONTINUE, this.goNext],
        [ACTION_PICK, this.selectPhotoFrame],
        [ACTION_SHOW_PHOTO_FRAMES, this.showPhotoFrames],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
